import React, {useEffect, useState, createContext, useContext } from "react";
import { req, log } from "./utils"; // Import req + remote logger

const DataContext = createContext(null);

function DataProvider({ children }) {
  const [isAdmin, ] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const statesAndTerritories = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' }
  ];

  // New state for machines
  const [machines, setMachines] = useState([]);
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [machineError, setMachineError] = useState(null);

  // Parts state
  const [parts, setParts] = useState([]);
  const [isLoadingParts, setIsLoadingParts] = useState(true);
  const [partsError, setPartsError] = useState(null);

  // Warehouses state
  const [warehouses, setWarehouses] = useState([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(true);
  const [warehouseError, setWarehouseError] = useState(null);

  // Technicians state
  const [technicians, setTechnicians] = useState([]);
  const [isLoadingTechnicians, setIsLoadingTechnicians] = useState(true);
  const [techniciansError, setTechniciansError] = useState(null);

  // Fetch machines, parts, warehouses, technicians when provider mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingMachines(true);
      setIsLoadingParts(true);
      setIsLoadingWarehouses(true);
      setIsLoadingTechnicians(true);
      setMachineError(null);
      setPartsError(null);
      setWarehouseError(null);
      setTechniciansError(null);

      const loadMachines = async () => {
        try {
          const machinesData = await req('get', 'https://app.bottlecycler.com/api/warehouse/get_machines_with_venues');
          if (Array.isArray(machinesData)) {
            setMachines(machinesData);
          } else {
            console.error("Fetched machine data is not an array:", machinesData);
            setMachines([]);
            setMachineError("Unexpected machine data format.");
            log('load_machines_bad_format', { responseType: typeof machinesData, sample: machinesData });
          }
        } catch (error) {
          console.error("Failed to fetch machines:", error);
          setMachineError("Failed to load machine data.");
          setMachines([]);
          log('load_machines_failed', error);
        } finally {
          setIsLoadingMachines(false);
        }
      };

      const loadParts = async () => {
        try {
          const partsData = await req('get', 'https://app.bottlecycler.com/api/warehouse/get_parts');
          if (Array.isArray(partsData)) {
            setParts(partsData);
          } else {
            console.error("Fetched parts data is not an array:", partsData);
            setParts([]);
            setPartsError("Unexpected parts data format.");
            log('load_parts_bad_format', { responseType: typeof partsData });
          }
        } catch (error) {
          console.error("Failed to fetch parts:", error);
          setPartsError("Failed to load parts data.");
          setParts([]);
          log('load_parts_failed', error);
        } finally {
          setIsLoadingParts(false);
        }
      };

      const loadWarehouses = async () => {
        try {
          const warehouseData = await req('get', 'https://app.bottlecycler.com/api/warehouse/get_warehouses');
          if (Array.isArray(warehouseData)) {
            setWarehouses(warehouseData);
          } else {
            console.error("Fetched warehouse data is not an array:", warehouseData);
            setWarehouses([]);
            setWarehouseError("Unexpected warehouse data format.");
            log('load_warehouses_bad_format', { responseType: typeof warehouseData, sample: warehouseData });
          }
        } catch (error) {
          console.error("Failed to fetch warehouses:", error);
          setWarehouseError("Failed to load warehouse data.");
          setWarehouses([]);
          log('load_warehouses_failed', error);
        } finally {
          setIsLoadingWarehouses(false);
        }
      };

      const loadTechnicians = async () => {
        try {
          const techData = await req('get', 'https://app.bottlecycler.com/api/warehouse/get_technicians');
          if (Array.isArray(techData)) {
            setTechnicians(techData);
          } else {
            console.error("Fetched technicians data is not an array:", techData);
            setTechnicians([]);
            setTechniciansError("Unexpected technicians data format.");
            log('load_technicians_bad_format', { responseType: typeof techData });
          }
        } catch (error) {
          console.error("Failed to fetch technicians:", error);
          setTechniciansError("Failed to load technicians data.");
          setTechnicians([]);
          log('load_technicians_failed', error);
        } finally {
          setIsLoadingTechnicians(false);
        }
      };

      // Load in parallel so a slow endpoint doesn't block machine selection / warehouse list
      await Promise.all([loadMachines(), loadParts(), loadWarehouses(), loadTechnicians()]);
    };

    fetchData();

    const handleOnlineStatusChange = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []); // Fetch data once when provider mounts

  // Function to update a machine's status and optionally venue_id in the local state
  const updateMachineStatus = (machineIdPk, newStatus, newVenueId = null) => {
    setMachines(currentMachines => 
      currentMachines.map(machine => { 
        if (machine.machine_id_pk === machineIdPk) {
          // Update status and venue_id if provided
          const updatedMachine = { ...machine, machine_status: newStatus };
          if (newVenueId !== null) {
            updatedMachine.venue_id = newVenueId;
            // Optionally update venue_name too if available/needed?
            // For now, just updating venue_id based on warehouse selection
          }
          console.log(`Updated machine ${machineIdPk} in context:`, updatedMachine);
          return updatedMachine;
        }
        return machine;
      })
    );
  };

  const contextValue = {
    isOnline,
    isAdmin,
	statesAndTerritories,
    machines,
    isLoadingMachines,
    machineError,
    parts,
    isLoadingParts,
    partsError,
    warehouses,
    isLoadingWarehouses,
    warehouseError,
    updateMachineStatus,
    technicians,
    isLoadingTechnicians,
    techniciansError,
  };

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
}

function useDataContext() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useDataContext must be used within a DataProvider");
  }
  return context;
}

export { DataProvider, useDataContext };
